import { FC, useMemo, useState } from 'react';
import humanizeDuration from 'humanize-duration';
import { chain, orderBy } from 'lodash';
import { twJoin } from 'tailwind-merge';

import { TestStatus } from '@dbt-labs/dbt-dag';
import {
  Icon,
  RyeconFreshnessError,
  RyeconFreshnessPassed,
  RyeconFreshnessStale,
  RyeconFreshnessUnknown,
  RyeconStatusSkipped,
  StatusRyecon,
  Tooltip,
} from '@dbt-labs/sourdough';

import {
  FreshnessStatus,
  ModelAppliedStateNestedNode,
  Quality,
  RunStatus,
  SourceAppliedStateNestedNode,
} from '@/__generated__/graphql';
import { DetailsSection } from '@/components/SectionWithCard';
import {
  RequestedExposureAppliedStateNode,
  useExposureAncestorsByEnvironment,
} from '@/hooks/discovery/exposure';
import { computeSourceFreshness } from '@/util/dbt/freshness';
import { CollapsibleSection } from '../CollapsibleSection';
import { EmbedDataHealthInstructions } from '../EmbedDataHealthInstructions';
import { ResourceStatusTable, ResourceWithStatus } from '../ResourceStatusTable';

interface ExposureStatusTileSectionImplProps {
  appliedExposure: RequestedExposureAppliedStateNode;
  environmentId: number;
  userId: number | null | 'loading';
}

// function nameFromId(uniqueId: string) {
//   return uniqueId.split('.').slice(2).join('.');
// }

const getWorstTestStatus = (
  testStatuses: Record<string, number> | null | undefined,
): TestStatus | null => {
  testStatuses = testStatuses || {};
  if ((testStatuses.error || 0) > 0) {
    return 'error';
  }
  if ((testStatuses.fail || 0) > 0) {
    return 'fail';
  }
  if ((testStatuses.warn || 0) > 0) {
    return 'warn';
  }
  return null;
};

const ExposureStatusTileSectionImpl: FC<ExposureStatusTileSectionImplProps> = ({
  appliedExposure,
  environmentId,
  userId,
}) => {
  const freshnessStatus = appliedExposure.freshnessStatus || FreshnessStatus.Unknown;
  const upstreamStats = appliedExposure?.upstreamStats || {};
  const freshnessStats = upstreamStats?.sources || {};
  const freshnessStatusCounts: Record<FreshnessStatus, number> = {
    [FreshnessStatus.Error]: freshnessStats.error || 0,
    [FreshnessStatus.Pass]: freshnessStats.pass || 0,
    [FreshnessStatus.Warn]: freshnessStats.warn || 0,
    [FreshnessStatus.Unknown]: freshnessStats.missing || 0,
    [FreshnessStatus.Skipped]: freshnessStats.skipped || 0,
    [FreshnessStatus.Outdated]: freshnessStats.outdated || 0,
    [FreshnessStatus.Unconfigured]: freshnessStats.unconfigured || 0,
  };

  const hasStaleData =
    freshnessStatusCounts[FreshnessStatus.Error] +
      freshnessStatusCounts[FreshnessStatus.Warn] >
    0;

  const modelStats = upstreamStats?.models || {};
  const modelStatusCounts: Record<RunStatus, number> = {
    [RunStatus.Error]: modelStats.error || 0,
    [RunStatus.Skipped]: modelStats.skipped || 0,
    [RunStatus.Success]: modelStats.success || 0,
    [RunStatus.Reused]: modelStats?.reused || 0,
  };

  const testStatusCounts: Record<TestStatus, number> = upstreamStats?.tests || {};
  const hasTestIssues =
    (testStatusCounts?.error || 0) +
      (testStatusCounts?.warn || 0) +
      (testStatusCounts?.fail || 0) >
    0;
  const resourcesWithTests = upstreamStats?.num_upstream_resources_with_tests || 0;
  const totalResources = upstreamStats?.num_upstream_resources || 0;
  const testCoverage = totalResources ? resourcesWithTests / totalResources : 0;

  const quality = appliedExposure?.quality || Quality.Unknown;
  const maxSnapshottedAtDate = appliedExposure?.maxSnapshottedAt;

  const [shouldGetAncestors, setShouldGetAncestors] = useState(false);

  const [ancestorsQuery] = useExposureAncestorsByEnvironment({
    environmentId: environmentId,
    userId: userId as number | null,
    uniqueId: appliedExposure.uniqueId,
    pause: !shouldGetAncestors || userId === 'loading',
  });

  const ancestors =
    ancestorsQuery.data?.environment?.applied?.exposures?.edges[0]?.node.ancestors;
  const staleData = useMemo(() => {
    if (!ancestors?.length) {
      return null;
    }

    const staleResources = ancestors
      .filter(
        (ancestor): ancestor is SourceAppliedStateNestedNode =>
          ancestor.__typename === 'SourceAppliedStateNestedNode' &&
          [FreshnessStatus.Error, FreshnessStatus.Warn].includes(
            computeSourceFreshness(ancestor.freshness) as FreshnessStatus,
          ),
      )
      .map(
        (source) =>
          ({
            name: `${source.sourceName}.${source.name}`,
            resourceType: 'source',
            status: computeSourceFreshness(source.freshness)?.toLowerCase(),
            uniqueId: source.uniqueId,
          }) as ResourceWithStatus,
      );

    const failedModels = ancestors
      .filter(
        (ancestor): ancestor is ModelAppliedStateNestedNode =>
          ancestor.__typename === 'ModelAppliedStateNestedNode' &&
          ancestor.executionInfo?.lastRunStatus === RunStatus.Error,
      )
      .map(
        (model) =>
          ({
            name: model.uniqueId,
            resourceType: 'model',
            status: 'error',
            uniqueId: model.uniqueId,
          }) as ResourceWithStatus,
      );

    return orderBy(
      [...staleResources, ...failedModels],
      ['resourceType', 'status', 'name'],
    );
  }, [ancestors]);

  const failedTestResources = useMemo(() => {
    if (!ancestors?.length) {
      return null;
    }

    return chain(ancestors)
      .filter(
        (a): a is SourceAppliedStateNestedNode =>
          a.__typename === 'ModelAppliedStateNestedNode' &&
          getWorstTestStatus(a.testStatuses) !== null,
      )
      .map(
        (m) =>
          ({
            name: m.name,
            resourceType: 'model',
            status: getWorstTestStatus(m.testStatuses),
            uniqueId: m.uniqueId,
          }) as ResourceWithStatus,
      )
      .orderBy(['status', 'name'])
      .value();
  }, [ancestors]);

  const snapshottedAgo = maxSnapshottedAtDate
    ? humanizeDuration(
        new Date().getTime() - new Date(maxSnapshottedAtDate).getTime(),
        {
          units: ['d', 'h', 'm', 's'],
          largest: 2,
          maxDecimalPoints: 2,
        },
      )
    : null;

  return (
    <ExposureStatusTileSectionDisplay
      appliedExposure={appliedExposure}
      freshnessStatus={freshnessStatus}
      quality={quality}
      staleData={staleData || []}
      testCoverage={testCoverage}
      failedTestResources={failedTestResources || []}
      modelStatusCounts={modelStatusCounts}
      freshnessStatusCounts={freshnessStatusCounts}
      testStatusCounts={testStatusCounts}
      hasStaleData={hasStaleData}
      hasTestIssues={hasTestIssues}
      snapshottedAgo={snapshottedAgo}
      maxSnapshottedAtDate={maxSnapshottedAtDate}
      setShouldGetAncestors={setShouldGetAncestors}
    />
  );
};

interface ExposureStatusTileSectionDisplayProps {
  appliedExposure: RequestedExposureAppliedStateNode;
  freshnessStatus: FreshnessStatus;
  quality: Quality;
  staleData: ResourceWithStatus[];
  testCoverage: number;
  failedTestResources: ResourceWithStatus[];
  modelStatusCounts: Record<RunStatus, number>;
  freshnessStatusCounts: Record<FreshnessStatus, number>;
  testStatusCounts: Record<TestStatus, number>;
  hasStaleData: boolean;
  hasTestIssues: boolean;
  snapshottedAgo: string | null;
  maxSnapshottedAtDate: string | null;
  setShouldGetAncestors: (value: boolean) => void;
}

export const ExposureStatusTileSectionDisplay: FC<
  ExposureStatusTileSectionDisplayProps
> = ({
  appliedExposure,
  freshnessStatus,
  quality,
  staleData,
  testCoverage,
  failedTestResources,
  modelStatusCounts,
  freshnessStatusCounts,
  testStatusCounts,
  hasStaleData,
  hasTestIssues,
  snapshottedAgo,
  maxSnapshottedAtDate,
  setShouldGetAncestors,
}) => (
  <>
    <DetailsSection
      heading="Data health"
      withCard={false}
      withHeading={
        <div className="ml-2 flex-1">
          {maxSnapshottedAtDate && (
            <Tooltip content={new Date(maxSnapshottedAtDate).toLocaleString()}>
              <span className="text-sm text-fgDecorative">
                Last check completed {snapshottedAgo} ago.
              </span>
            </Tooltip>
          )}
        </div>
      }
    >
      <div className="overflow-hidden rounded-md border border-borderMuted">
        <DataFreshnessSection
          freshnessStatus={freshnessStatus}
          freshnessStatusCounts={freshnessStatusCounts}
          staleData={staleData}
          hasStaleData={hasStaleData}
          hasTestIssues={hasTestIssues}
          setShouldGetAncestors={setShouldGetAncestors}
        />
        <DataQualitySection
          quality={quality}
          modelStatusCounts={modelStatusCounts}
          testStatusCounts={testStatusCounts}
          testCoverage={testCoverage}
          failedTestResources={failedTestResources}
          hasStaleData={hasStaleData}
          hasTestIssues={hasTestIssues}
          setShouldGetAncestors={setShouldGetAncestors}
        />
      </div>
    </DetailsSection>
    {appliedExposure.exposureType === 'dashboard' && (
      <EmbedDataHealthInstructions
        className="-mt-8"
        uniqueId={appliedExposure.uniqueId}
        dashboardUri={appliedExposure.url}
        autoBiProvider={appliedExposure.autoBiProvider}
        biResourceId={appliedExposure.meta?.dbt_cloud_bi_resource_id}
      />
    )}
  </>
);

const freshnessToDataQualityInfo: Record<FreshnessStatus, DataMessage> = {
  [FreshnessStatus.Pass]: {
    ryecon: RyeconFreshnessPassed,
    text: 'Data is fresh',
  },
  [FreshnessStatus.Warn]: {
    ryecon: RyeconFreshnessStale,
    text: 'Data may be stale',
  },
  [FreshnessStatus.Error]: {
    ryecon: RyeconFreshnessError,
    text: 'Data is stale',
  },
  [FreshnessStatus.Unknown]: {
    ryecon: RyeconFreshnessUnknown,
    text: 'Data freshness unknown',
    className: 'text-fgDecorative',
  },
  [FreshnessStatus.Skipped]: {
    ryecon: RyeconStatusSkipped,
    text: 'Data freshness skipped',
    className: 'text-bgDuotoneNeutral',
  },
  [FreshnessStatus.Outdated]: {
    ryecon: RyeconFreshnessStale,
    text: 'Data freshness not run recently',
  },
  [FreshnessStatus.Unconfigured]: {
    ryecon: RyeconFreshnessUnknown,
    text: 'Data freshness unconfigured',
  },
};

const qualityToInfo = (quality: Quality): DataMessage => {
  switch (quality) {
    case Quality.Pass:
      return {
        ryecon: RyeconFreshnessPassed,
        text: 'Data quality passed',
      };
    case Quality.Warn:
      return {
        ryecon: RyeconFreshnessStale,
        text: 'Data quality warning',
      };
    case Quality.Unknown:
      return {
        ryecon: RyeconFreshnessUnknown,
        text: 'Data quality unknown',
      };
    case Quality.Error:
      return {
        ryecon: RyeconFreshnessError,
        text: 'Data quality degraded',
      };
  }
};

type DataMessage = {
  ryecon: StatusRyecon;
  text: string;
  className?: string;
};

const DataQualityMessage: React.FC<DataMessage> = ({ ryecon, text, className }) => {
  return (
    <>
      {ryecon}
      <Icon
        size="lg"
        ryecon={ryecon}
        className={twJoin('flex-shrink-0 align-middle', className)}
      />
      <span className="ml-2 whitespace-nowrap pr-3 align-middle text-base font-semibold text-fgMain">
        {text}
      </span>
    </>
  );
};

interface DataFreshnessSectionProps {
  freshnessStatus: FreshnessStatus;
  staleData: ResourceWithStatus[];
  hasStaleData: boolean;
  hasTestIssues: boolean;
  setShouldGetAncestors: (value: boolean) => void;
  freshnessStatusCounts: Record<FreshnessStatus, number>;
}

export const DataFreshnessSection: FC<DataFreshnessSectionProps> = ({
  freshnessStatus,
  freshnessStatusCounts,
  staleData,
  hasStaleData,
  hasTestIssues,
  setShouldGetAncestors,
}) => (
  <CollapsibleSection
    className="-mb-[1px] mt-1 flex w-full overflow-hidden border-b border-borderMuted p-4 text-fgMain"
    closeAltText="Hide data freshness details"
    expandAltText="Show data freshness details"
    disable={!hasStaleData}
    shouldIndent={hasStaleData || hasTestIssues}
    onToggle={() => {
      setShouldGetAncestors(true);
    }}
    toExpand={<ResourceStatusTable resources={staleData || []} />}
    data-testid="data-freshness-section"
  >
    <DataQualityMessage {...freshnessToDataQualityInfo[freshnessStatus]} />
    <span className="flex-1 truncate text-right leading-6 text-fgAlt">
      <span>
        <b> Sources: </b>
        {freshnessStatusCounts.Pass || 0} fresh, {freshnessStatusCounts.Warn || 0}{' '}
        delayed, {freshnessStatusCounts.Error || 0} stale,{' '}
        {freshnessStatusCounts.Skipped || 0} skipped,{' '}
        {freshnessStatusCounts.Outdated || 0} outdated,{' '}
        {freshnessStatusCounts.Unconfigured || 0} unconfigured
      </span>
    </span>
  </CollapsibleSection>
);

interface DataQualitySectionProps {
  quality: Quality;
  modelStatusCounts: Record<RunStatus, number>;
  testStatusCounts: Record<TestStatus, number>;
  testCoverage: number;
  failedTestResources: ResourceWithStatus[];
  hasStaleData: boolean;
  hasTestIssues: boolean;
  setShouldGetAncestors: (value: boolean) => void;
}

export const DataQualitySection: FC<DataQualitySectionProps> = ({
  quality,
  modelStatusCounts,
  testStatusCounts,
  testCoverage,
  failedTestResources,
  hasStaleData,
  hasTestIssues,
  setShouldGetAncestors,
}) => (
  <CollapsibleSection
    className="flex w-full overflow-hidden border-t border-borderMuted p-4 pt-5 text-fgMain"
    closeAltText="Hide data freshness details"
    expandAltText="Show data freshness details"
    disable={!hasTestIssues}
    shouldIndent={hasStaleData || hasTestIssues}
    onToggle={() => setShouldGetAncestors(true)}
    toExpand={
      <ResourceStatusTable resources={failedTestResources || []} className="-mb-0.5" />
    }
    data-testid="data-quality-section"
  >
    <DataQualityMessage {...qualityToInfo(quality)} />
    <span className="flex-1 truncate text-right leading-6 text-fgAlt">
      <span className="mr-2 border-r border-borderMuted pr-2">
        <b>Models: </b>
        {modelStatusCounts.success || 0} success, {modelStatusCounts.error || 0}{' '}
        failure, {modelStatusCounts.reused || 0} reused,{' '}
        {modelStatusCounts.skipped || 0} skipped
      </span>
      <span className="mr-2 border-r border-borderMuted pr-2">
        <b>Tests: </b>
        {testStatusCounts.pass || 0} success, {testStatusCounts.warn || 0} warn,{' '}
        {testStatusCounts.error || 0} error
      </span>
      <span>
        <b>Test coverage: </b>
        {`${(testCoverage * 100).toFixed(0)}%`}
      </span>
    </span>
  </CollapsibleSection>
);

interface ExposureStatusTileSectionProps {
  appliedExposure: RequestedExposureAppliedStateNode | null | undefined;
  environmentId: number;
  userId: number | null | 'loading';
}

export const ExposureStatusTileSection: FC<ExposureStatusTileSectionProps> = ({
  appliedExposure,
  environmentId,
  userId,
}) => {
  if (!appliedExposure) {
    return null;
  }
  return (
    <ExposureStatusTileSectionImpl
      appliedExposure={appliedExposure}
      environmentId={environmentId}
      userId={userId}
    />
  );
};
